import React, { useState, useEffect } from 'react';
import {
  Box, Heading, VStack, FormControl, FormLabel, Input, Button, SimpleGrid, useToast, Spinner, Flex,
  Tabs, TabList, Tab, TabPanels, TabPanel, Text, Icon,
  NumberInput, NumberInputField, NumberInputStepper, NumberIncrementStepper, NumberDecrementStepper,
  Slider, SliderTrack, SliderFilledTrack, SliderThumb, Card, CardHeader, CardBody, Stat, StatLabel, StatNumber, Alert, AlertIcon,
  Badge, useColorModeValue
} from '@chakra-ui/react';
import { useSettings } from '../hooks/useSettings';
import { getConfig, updateConfig, saveDigitalTwinSettings } from '../services/settingsService';
import { FaUpload } from 'react-icons/fa';
import { MdAutoAwesome } from 'react-icons/md';
import { updateAssetTypeModel } from '../services/assetTypeService';

// Component for a single model upload section
const ModelUploader = ({ assetType }) => {
  const toast = useToast();
  const [file, setFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleUpload = async () => {
    if (!file) {
      toast({ title: "No file selected", status: "warning", isClosable: true });
      return;
    }
    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append('model', file);
      await updateAssetTypeModel(assetType, formData);
      toast({ title: "Model uploaded successfully!", status: "success", isClosable: true });
    } catch (error) {
      toast({ title: "Upload failed", description: error.message, status: "error", isClosable: true });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box p={4} borderWidth="1px" borderRadius="md">
      <FormControl>
        <FormLabel>{assetType.replace(/_/g, ' ').toUpperCase()} Model</FormLabel>
        <Input type="file" accept=".glb" onChange={handleFileChange} p={1} />
        <Button
          mt={2}
          leftIcon={<Icon as={FaUpload} />}
          colorScheme="blue"
          onClick={handleUpload}
          isLoading={isLoading}
          isDisabled={!file}
        >
          Upload
        </Button>
      </FormControl>
    </Box>
  );
};

// Enhanced AI Dispatch Settings Component
const AIDispatchSettings = () => {
    const [weights, setWeights] = useState({ distance: 0.5, skill: 0.3, workload: 0.2 });
    const [autoDispatchThreshold, setAutoDispatchThreshold] = useState(90); 

    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const toast = useToast();

    const weightsConfigKey = 'dispatch_optimizer_weights';
    const thresholdConfigKey = 'auto_dispatch_threshold';

    useEffect(() => {
        const fetchConfigs = async () => {
            setIsLoading(true);
            
            // Fetch weights safely
            try {
                const responseWeights = await getConfig(weightsConfigKey);
                if (responseWeights && responseWeights.config_value) {
                    setWeights(responseWeights.config_value);
                }
            } catch (err) {
                console.warn('[Settings] Weights config not found or failed to load. Using defaults.');
            }

            // Fetch auto-dispatch threshold safely
            try {
                const responseThreshold = await getConfig(thresholdConfigKey);
                if (responseThreshold && responseThreshold.config_value !== undefined) {
                    const val = responseThreshold.config_value.value !== undefined 
                        ? responseThreshold.config_value.value 
                        : responseThreshold.config_value;
                    setAutoDispatchThreshold(Number(val));
                }
            } catch (err) {
                console.warn('[Settings] Threshold config not found or failed to load. Using defaults.');
            }
            
            setIsLoading(false);
        };
        fetchConfigs();
    }, []);

    const handleSliderChange = (value, key) => {
        setWeights(prev => ({ ...prev, [key]: value }));
    };

    const handleSave = async () => {
        setIsSaving(true);
        const total = Object.values(weights).reduce((sum, val) => sum + val, 0);
        if (Math.abs(total - 1.0) > 0.01) {
            toast({
                title: 'Invalid Weights',
                description: 'The sum of all weights must be equal to 1.0.',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
            setIsSaving(false);
            return;
        }

        try {
            await updateConfig(weightsConfigKey, weights);
            await updateConfig(thresholdConfigKey, { value: autoDispatchThreshold });

            toast({
                title: 'Settings Saved',
                description: 'AI dispatch configurations have been updated successfully.',
                status: 'success',
                duration: 5000,
                isClosable: true,
            });
        } catch (err) {
            toast({
                title: 'Save Failed',
                description: err.message || 'Could not save AI dispatch settings.',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setIsSaving(false);
        }
    };

    const zeroTouchBg = useColorModeValue('purple.50', 'rgba(128, 90, 213, 0.1)');
    const zeroTouchBorder = useColorModeValue('purple.200', 'purple.700');
    const zeroTouchText = useColorModeValue('purple.800', 'purple.200');

    if (isLoading) {
        return <Flex justify="center" p={5}><Spinner /></Flex>;
    }

    return (
        <Card>
            <CardHeader>
                <Heading size="md">✨ AI Dispatch Optimizer Settings</Heading>
            </CardHeader>
            <CardBody>
                <VStack spacing={8} align="stretch">
                    
                    <Box p={5} borderWidth="1px" borderRadius="lg" bg={zeroTouchBg} borderColor={zeroTouchBorder}>
                        <Flex justify="space-between" align="center" mb={2}>
                            <Heading size="sm" color={zeroTouchText}>🚀 Zero-Touch Auto-Dispatch Threshold</Heading>
                            <Badge colorScheme={autoDispatchThreshold >= 90 ? 'green' : autoDispatchThreshold >= 80 ? 'yellow' : 'orange'} fontSize="md" px={2} py={1} borderRadius="md">
                                {autoDispatchThreshold}%
                            </Badge>
                        </Flex>
                        <Text fontSize="sm" mb={4} color={zeroTouchText}>
                            If the ML Engine matches a technician to a new Work Order with a confidence score equal to or higher than this threshold, it will bypass human review and dispatch the technician immediately.
                        </Text>
                        <FormControl>
                            <Slider 
                                value={autoDispatchThreshold} 
                                min={60} 
                                max={99} 
                                step={1} 
                                onChange={(val) => setAutoDispatchThreshold(val)}
                                colorScheme="purple"
                            >
                                <SliderTrack><SliderFilledTrack /></SliderTrack>
                                <SliderThumb boxSize={6}>
                                    <Icon as={MdAutoAwesome} color="purple.500" />
                                </SliderThumb>
                            </Slider>
                            <Flex justify="space-between" mt={1}>
                                <Text fontSize="xs" color="gray.500" fontWeight="bold">60% (High Automation, Lower Precision)</Text>
                                <Text fontSize="xs" color="gray.500" fontWeight="bold">99% (Low Automation, Strict Precision)</Text>
                            </Flex>
                        </FormControl>
                    </Box>

                    <Box>
                        <Heading size="sm" mb={4}>⚖️ Optimization Weights</Heading>
                        <Text mb={4}>Adjust the importance of different factors when the AI scores and suggests technicians. All weights must add up to exactly 1.0.</Text>
                        
                        <VStack spacing={6} align="stretch">
                            <FormControl>
                                <FormLabel>Distance Importance: {weights.distance.toFixed(2)}</FormLabel>
                                <Slider value={weights.distance} min={0} max={1} step={0.05} onChange={(val) => handleSliderChange(val, 'distance')}>
                                    <SliderTrack><SliderFilledTrack /></SliderTrack>
                                    <SliderThumb />
                                </Slider>
                            </FormControl>

                            <FormControl>
                                <FormLabel>Skill Match Importance: {weights.skill.toFixed(2)}</FormLabel>
                                <Slider value={weights.skill} min={0} max={1} step={0.05} onChange={(val) => handleSliderChange(val, 'skill')}>
                                    <SliderTrack><SliderFilledTrack /></SliderTrack>
                                    <SliderThumb />
                                </Slider>
                            </FormControl>

                            <FormControl>
                                <FormLabel>Workload Balance Importance: {weights.workload.toFixed(2)}</FormLabel>
                                <Slider value={weights.workload} min={0} max={1} step={0.05} onChange={(val) => handleSliderChange(val, 'workload')}>
                                    <SliderTrack><SliderFilledTrack /></SliderTrack>
                                    <SliderThumb />
                                </Slider>
                            </FormControl>
                        </VStack>
                    </Box>

                    <Flex justify="space-between" align="center" pt={4} borderTopWidth="1px">
                        <Stat>
                            <StatLabel>Total Weight</StatLabel>
                            <StatNumber color={Math.abs(Object.values(weights).reduce((s, v) => s + v, 0) - 1.0) > 0.01 ? 'red.500' : 'green.500'}>
                                {Object.values(weights).reduce((s, v) => s + v, 0).toFixed(2)} / 1.00
                            </StatNumber>
                        </Stat>
                        <Button colorScheme="blue" onClick={handleSave} isLoading={isSaving} size="lg">
                            Save AI Settings
                        </Button>
                    </Flex>
                </VStack>
            </CardBody>
        </Card>
    );
};

const SettingsPage = () => {
  const { settings, setSettings, loading } = useSettings(); 
  const toast = useToast();
  const [isSaving, setIsSaving] = useState(false);

  const [psiMin, setPsiMin] = useState('');
  const [psiMax, setPsiMax] = useState('');

  useEffect(() => {
    if (settings) {
      setPsiMin(settings.psi_normal_range_min || '1.0');
      setPsiMax(settings.psi_normal_range_max || '8.0');
    }
  }, [settings]);

  const handleColorChange = (status, color) => {
    setSettings(prev => ({
      ...prev,
      statusColors: { ...prev.statusColors, [status]: color },
    }));
  };

  const handleSaveChanges = async () => {
    setIsSaving(true);
    try {
      if (settings?.statusColors) {
          await updateConfig('digital_twin_status_colors', { statusColors: settings.statusColors });
      }
      await updateConfig('psi_normal_range_min', { value: psiMin });
      await updateConfig('psi_normal_range_max', { value: psiMax });
      
      toast({
        title: 'Settings saved.',
        status: 'success',
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error saving settings.',
        description: error.message,
        status: 'error',
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return <Flex justify="center" align="center" h="50vh"><Spinner size="xl" /></Flex>;
  }

  return (
    <Box p={8}>
      <VStack spacing={8} align="stretch">
        <Heading>System Settings</Heading>
        
        <Tabs variant="soft-rounded" colorScheme="blue">
          <TabList>
            <Tab>AI Settings</Tab>
            <Tab>Digital Twin Colors</Tab>
            <Tab>3D Models</Tab>
            <Tab>Analytics Settings</Tab>
          </TabList>
          
          <TabPanels>
            <TabPanel>
              <AIDispatchSettings />
            </TabPanel>
            
            <TabPanel>
              <VStack spacing={4} align="stretch">
                <Text>Define the colors for different asset statuses in the 3D view.</Text>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  {Object.entries(settings?.statusColors || {}).map(([status, color]) => (
                    <FormControl key={status}>
                      <FormLabel>{status.replace(/_/g, ' ').toUpperCase()}</FormLabel>
                      <Input type="color" value={color} onChange={(e) => handleColorChange(status, e.target.value)} />
                    </FormControl>
                  ))}
                </SimpleGrid>
              </VStack>
            </TabPanel>
            
            <TabPanel>
              <VStack spacing={4} align="stretch">
                <Text>Upload the default .glb model file for each asset type.</Text>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <ModelUploader assetType="street_light" />
                  <ModelUploader assetType="traffic_light" />
                  <ModelUploader assetType="sensor" />
                  <ModelUploader assetType="pump" />
                </SimpleGrid>
              </VStack>
            </TabPanel>
            
            <TabPanel>
              <VStack spacing={4} align="stretch">
                <Heading size="md">Predictive Maintenance</Heading>
                <Text>Define the normal operating pressure (PSI) range for sensors. Readings outside this range will negatively impact an asset's health score.</Text>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <FormControl>
                    <FormLabel>Normal PSI Range (Min)</FormLabel>
                    <NumberInput value={psiMin} onChange={(valueString) => setPsiMin(valueString)} precision={1} step={0.1}>
                      <NumberInputField />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  </FormControl>
                  <FormControl>
                    <FormLabel>Normal PSI Range (Max)</FormLabel>
                    <NumberInput value={psiMax} onChange={(valueString) => setPsiMax(valueString)} precision={1} step={0.1}>
                      <NumberInputField />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  </FormControl>
                </SimpleGrid>
              </VStack>
            </TabPanel>
            
          </TabPanels>
        </Tabs>
        
        <Box>
          <Button colorScheme="blue" onClick={handleSaveChanges} isLoading={isSaving} width="full" size="lg">
            Save System Settings
          </Button>
        </Box>
      </VStack>
    </Box>
  );
};

export default SettingsPage;